import { format as formatDate } from 'date-fns';

import { getScopedTranslate } from '@locale/translate';

import { getDateFnsLocale } from './format-languages';

export { FORMAT_LANGUAGES as FORMAT_LANGUAGE_OPTIONS, getDateFnsLocale } from './format-languages';

export const SAMPLE_DATE = new Date(new Date().getFullYear(), 8, 1);

export const getFormatPreview = (format: string, formatLanguage?: string): string => {
  if (!format) return '';
  try {
    const locale = formatLanguage ? getDateFnsLocale(formatLanguage) : undefined;
    return formatDate(SAMPLE_DATE, format, locale ? { locale } : undefined);
  } catch {
    return format;
  }
};

export const isMatchingPreset = (
  preset: { format: string; formatLanguage?: string },
  format?: string,
  formatLanguage?: string
): boolean =>
  preset.format === format &&
  (preset.formatLanguage || DEFAULT_FORMAT_LANGUAGE) ===
    (formatLanguage || DEFAULT_FORMAT_LANGUAGE);

export const getFormatPresetLabel = (
  format?: string,
  formatLanguage?: string,
  presets?: { name: string; format: string; formatLanguage?: string }[]
): string => {
  if (!format || format === 'undefined') return 'Default';
  const preset = presets?.find((preset) => isMatchingPreset(preset, format, formatLanguage));
  return preset?.name ?? 'Custom';
};

export const isFormattableType = (type?: string): boolean => type === 'date';

/**
 * Builds the node attribute patch for a format change. When the format is
 * cleared (Default selected) the language is also cleared, so the backend
 * doesn't render the company-default format with a stale locale. When the
 * format is set without a language, the existing language is left alone.
 */
export const buildFormatAttributeUpdates = (
  format: string,
  formatLanguage?: string
): Record<string, string | undefined> => {
  const normalizedFormat = format || undefined;
  const updates: Record<string, string | undefined> = { format: normalizedFormat };
  if (!normalizedFormat || formatLanguage !== undefined) {
    updates.formatLanguage = formatLanguage ?? undefined;
  }
  return updates;
};

export type TokenCategory = 'day' | 'month' | 'year';

export type FormatTokenBadge = {
  display: string;
  token: string;
  category: TokenCategory;
};

export const FORMAT_TOKEN_BADGES: FormatTokenBadge[] = [
  { display: 'D', token: 'd', category: 'day' },
  { display: 'DD', token: 'dd', category: 'day' },
  { display: 'DDD', token: 'EEE', category: 'day' },
  { display: 'DDDD', token: 'EEEE', category: 'day' },
  { display: 'M', token: 'M', category: 'month' },
  { display: 'MM', token: 'MM', category: 'month' },
  { display: 'MMM', token: 'MMM', category: 'month' },
  { display: 'MMMM', token: 'MMMM', category: 'month' },
  { display: 'YY', token: 'yy', category: 'year' },
  { display: 'YYYY', token: 'yyyy', category: 'year' },
];

export type SeparatorBadge = {
  display: string;
  value: string;
};

export const getSeparatorBadges = (): SeparatorBadge[] => {
  const { t } = getScopedTranslate(locale);
  return [
    { display: '/', value: '/' },
    { display: '•', value: '•' },
    { display: '-', value: '-' },
    { display: ',', value: ',' },
    { display: '.', value: '.' },
    { display: ':', value: ':' },
    { display: t('space'), value: ' ' },
  ];
};

export const DEFAULT_FORMAT_LANGUAGE = 'en_US';

const SORTED_TOKENS = [...FORMAT_TOKEN_BADGES].sort((a, b) => b.token.length - a.token.length);

let nextId = 0;
export const generateCompositionId = () =>
  `composition-${nextId++}-${Math.random().toString(36).slice(2, 6)}`;

export type CompositionItem = {
  id: string;
  type: 'token' | 'separator';
  display: string;
  value: string;
  category?: TokenCategory;
};

export const parseFormatString = (formatString: string): CompositionItem[] => {
  if (!formatString) return [];
  const items: CompositionItem[] = [];
  let index = 0;

  while (index < formatString.length) {
    let matched = false;

    for (const badge of SORTED_TOKENS) {
      if (formatString.slice(index).startsWith(badge.token)) {
        items.push({
          id: generateCompositionId(),
          type: 'token',
          display: badge.display,
          value: badge.token,
          category: badge.category,
        });
        index += badge.token.length;
        matched = true;
        break;
      }
    }

    if (!matched) {
      const character = formatString[index];
      const separator = getSeparatorBadges().find((separator) => separator.value === character);
      items.push({
        id: generateCompositionId(),
        type: 'separator',
        display: separator?.display ?? character,
        value: character,
      });
      index++;
    }
  }

  return items;
};

const locale = {
  en: {
    space: 'Space',
  },
};
